<script setup lang="ts" generic="T extends string">
type TabItem = {
  value: T;
  label: string;
  icon?: string;
  id?: string;
  ariaControls?: string;
};

const model = defineModel<T>({ required: true });

const props = defineProps<{
  items: TabItem[];
  ariaLabel: string;
}>();

function onKeydown(event: KeyboardEvent) {
  const values = props.items.map((it) => it.value);
  const current = values.indexOf(model.value);
  let next: number | null = null;
  if (event.key === "ArrowRight") next = (current + 1) % values.length;
  else if (event.key === "ArrowLeft") next = (current - 1 + values.length) % values.length;
  else if (event.key === "Home") next = 0;
  else if (event.key === "End") next = values.length - 1;
  if (next === null) return;
  event.preventDefault();
  model.value = values[next]!;
  const root = event.currentTarget as HTMLElement;
  nextTick(() => root.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next!]?.focus());
}
</script>

<template>
  <UFieldGroup role="tablist" :aria-label="ariaLabel" @keydown="onKeydown">
    <UButton
      v-for="item in items"
      :id="item.id"
      :key="item.value"
      role="tab"
      :color="model === item.value ? 'blue' : 'primary'"
      :variant="model === item.value ? 'solid' : 'soft'"
      :aria-selected="model === item.value"
      :aria-controls="item.ariaControls"
      :tabindex="model === item.value ? 0 : -1"
      :icon="item.icon"
      size="xs"
      :ui="{ base: 'aria-selected:pointer-events-none not-aria-selected:hover:bg-blue-400', leadingIcon: 'size-4' }"
      @click="() => { model = item.value }"
    >
      {{ item.label }}
    </UButton>
  </UFieldGroup>
</template>
